import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import AuthPage from '../views/AuthView.vue'
import { fetchAuthSession, fetchUserAttributes, updateUserAttributes } from 'aws-amplify/auth'
import { getCurrentUser } from 'aws-amplify/auth'
import { useAuthStore } from '../stores/auth.js'
import { useCatalogStore } from '../stores/catalog.js'
import { redirectToLogin } from '../utils/authSession.js'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/auth',
      component: AuthPage
    },
    {
      path: '/calendario',
      name: 'calendario',
      meta: {
        auth: true,
        reload: true,
        title: 'Calendario'
      },
      component: () => import('../views/CalendarHeatmapView.vue')
    },
    {
      path: '/resumen',
      name: 'resumen',
      meta: {
        auth: true,
        reload: true,
        title: 'Resumen'
      },
      component: () => import('../views/ResumenNativeView.vue')
    },
    {
      path: '/config',
      name: 'config',
      meta: {
        auth: true,
        reload: true,
        title: 'Configuración'
      },
      component: () => import('../views/ConfigNativeView.vue')
    },
    {
      path: '/semana',
      name: 'semana',
      meta: {
        auth: true,
        reload: true,
        title: 'Carga Semanal'
      },
      component: () => import('../views/CargaSemanal.vue')
    },
    {
      path: '/carga/:fecha?',
      name: 'carga',
      meta: {
        auth: true,
        reload: true,
        title: 'Carga de Horas'
      },
      component: () => import('../views/CargaView.vue')
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      meta: {
        auth: true,
        reload: true,
        title: 'Dashboard'
      },
      component: () => import('../views/DashboardView.vue')
    },
    {
      path: '/',
      redirect: '/carga'
    },
    {
      path: '/:fecha?',
      name: 'home',
      component: HomeView,
      meta: {
        auth: true,
        reload: true,
        title: 'TimeSheet'
      }
    }
  ]
})

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL

// Fetch autenticado para el router
async function authenticatedFetch(url) {
  const session = await fetchAuthSession()
  const token = session.tokens?.idToken?.toString()
  return fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
}

router.beforeResolve(async (to, from, next) => {
  if (to.meta.auth) {
    try {
      const authStore = useAuthStore()
      const hasCachedData = authStore.userId && authStore.role && authStore.usuario
      const comingFromAuth = from.path === '/auth'

      // Login real (recién viene de /auth, o no había nada cacheado todavía)
      // - se marca acá, no en cada refresh de token, para que el
      // auto-logout proactivo (ver initSessionWatchdog() en authSession.js)
      // cuente las horas desde el login de verdad, no desde el último
      // refresh. `comingFromAuth` siempre lo re-marca (re-login de verdad);
      // si no había loginAt todavía (ej. se limpió localStorage a mano, o la
      // sesión ya estaba cacheada de antes de que existiera este campo) se
      // marca una vez igual para no dejarlo sin arrancar nunca. Tiene que
      // evaluarse ANTES del camino rápido de abajo: un usuario con sesión ya
      // cacheada toma siempre ese camino y nunca vuelve a pasar por acá, así
      // que si el backfill quedara después del `return` quedaría muerto para
      // justamente esos usuarios (su loginAt nunca se setea, y el watchdog
      // jamás los desloguea).
      if (comingFromAuth || !authStore.loginAt) authStore.setLoginAt(Date.now())

      if (hasCachedData && !comingFromAuth) {
        // Validar sesión Amplify (rápido, sin fetch de attrs)
        await getCurrentUser()

        // Cambio 4: Pre-lanzar catálogos para que estén en vuelo cuando monte la vista
        const catalogStore = useCatalogStore()
        catalogStore.ensureTareas()
        catalogStore.ensureProyectos()

        next()
        return
      }

      // Cambio 3: Carga completa — paralelizar getCurrentUser + fetchUserAttributes
      let user, userAttributes
      try {
        ;[user, userAttributes] = await Promise.all([
          getCurrentUser(),
          fetchUserAttributes().catch(attrError => {
            console.error('Error obteniendo atributos:', attrError)
            return {}
          })
        ])
      } catch (err) {
        throw err
      }

      // Guardar el UUID de Cognito
      authStore.setUsuario(user.username)

      // Guardar el email
      const email = user.signInDetails?.loginId || userAttributes.email || user.username
      authStore.setUserMail(email)

      // Guardar el nombre
      authStore.setNombre(userAttributes.preferred_username || user.username)

      // Cambio 2: Usar custom:srk_role de Cognito si existe, evitando llamada a /UserCognito
      const cachedSrkId = userAttributes['custom:srk_id']
      const cachedSrkRole = userAttributes['custom:srk_role']

      if (cachedSrkId && cachedSrkRole) {
        // Tenemos ambos en Cognito, no necesitamos llamar al API
        authStore.setUserId(cachedSrkId)
        authStore.setRole(cachedSrkRole)
      } else if (cachedSrkId && !cachedSrkRole) {
        // Tenemos ID pero no rol: llamar API solo para obtener rol y cachearlo
        authStore.setUserId(cachedSrkId)

        const response = await authenticatedFetch(apiBaseUrl + '/UserCognito/' + user.username)
        const data = await response.json()

        // El endpoint responde {success, data: {...un solo usuario...}, message} -
        // data acá NO es un array (antes se chequeaba data.length/data[0], que
        // nunca son verdaderos en un objeto, así que esta rama siempre daba 'none').
        const role = (data?.success && data.data?.us_IsAdmin == 1) ? 'admin' : 'none'
        authStore.setRole(role)

        // Cachear rol en Cognito para futuros logins
        try {
          await updateUserAttributes({
            userAttributes: {
              'custom:srk_role': role
            }
          })
        } catch (err) {
          console.error('Error cacheando rol en Cognito:', err)
        }
      } else {
        // No tenemos nada en Cognito: primera vez, carga completa
        const response = await authenticatedFetch(apiBaseUrl + '/UserCognito/' + user.username)

        if (!response.ok) {
          throw new Error('Error al obtener datos del usuario')
        }

        const data = await response.json()

        // Mismo formato que arriba: {success, data: {...un solo usuario...}}.
        // Con data.length/data[0] (formato viejo, array) esta condición
        // siempre era falsa para CUALQUIER usuario nuevo (nunca cacheó nada
        // en Cognito todavía, así que siempre pasa por esta rama) - por eso
        // "no puede ingresar" apenas se crea el usuario, aunque sí exista
        // en la base (el fetch ya lo confirma con success:true).
        if (data?.success && data.data) {
          const usuario = data.data
          authStore.setUserId(usuario.us_Id)

          const role = usuario.us_IsAdmin == 1 ? 'admin' : 'none'
          authStore.setRole(role)

          try {
            await updateUserAttributes({
              userAttributes: {
                'custom:srk_id': usuario.us_Id.toString(),
                'custom:srk_role': role
              }
            })
          } catch (err) {
            console.error('Error actualizando atributos:', err)
          }
        } else {
          console.error('Usuario no encontrado en la base de datos')
          authStore.setRole('none')
          throw new Error('Usuario no registrado en el sistema')
        }
      }

      // Cambio 4: Pre-lanzar catálogos (fire-and-forget) mientras la vista monta
      const catalogStore = useCatalogStore()
      catalogStore.ensureTareas()
      catalogStore.ensureProyectos()

      next()
    } catch (err) {
      await redirectToLogin()
      next(false)
    }
  } else {
    next()
  }
})

export default router
